/**
 * The header every app shares: brand lockup, app nav, theme switcher, account
 * menu. Lives here rather than in each SPA so the three apps cannot drift.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { BrandLockup, ThemeToggle } from "@cqt/brand";
import { useAuth } from "./auth";

export interface NavItem {
  label: string;
  /** Absolute path under the platform host, or an in-app route. */
  href: string;
  /** True when this item is the current page. */
  current?: boolean;
}

function initials(name: string | null, email: string): string {
  const source = (name ?? "").trim() || email;
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  return (parts[0]?.[0] ?? "?").concat(parts[1]?.[0] ?? "").toUpperCase();
}

function AccountMenu() {
  const { me, logout, registerPasskey, accountUrl } = useAuth();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrap} style={{ position: "relative" }}>
      <button
        className="avatar"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={() => setOpen((v) => !v)}
      >
        {initials(me.user.full_name, me.user.email)}
      </button>

      {open ? (
        <div className="menu" role="menu">
          <div className="menu__head">
            <div className="menu__name">{me.user.full_name ?? me.user.email}</div>
            <div className="menu__sub mono">{me.user.email}</div>
            <div className="menu__sub" style={{ marginTop: 6 }}>
              {me.company.name}
              {me.is_superadmin ? <span className="chip chip--accent" style={{ marginLeft: 8 }}>Superadmin</span> : null}
            </div>
          </div>
          <div className="menu__body">
            <div className="menu__row">
              <span className="label" style={{ letterSpacing: "0.12em" }}>Theme</span>
              <ThemeToggle />
            </div>
            {me.roles.length > 0 ? (
              <div className="menu__row" style={{ alignItems: "flex-start" }}>
                <span className="label" style={{ letterSpacing: "0.12em" }}>Roles</span>
                <span style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {me.roles.map((r) => (
                    <span key={r.id} className="chip">{r.name}</span>
                  ))}
                </span>
              </div>
            ) : null}
          </div>
          <div className="menu__foot">
            <button className="btn btn--ghost btn--sm" role="menuitem" onClick={() => void registerPasskey()}>
              Set up passkey
            </button>
            <a className="btn btn--ghost btn--sm" href={accountUrl} target="_blank" rel="noreferrer noopener">
              Keycloak account
            </a>
            <button className="btn btn--outline btn--sm" role="menuitem" onClick={logout}>
              Sign out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * @param app     short app label shown next to the wordmark, e.g. "CRM"
 * @param nav     header links; omit for the portal
 * @param actions extra controls rendered before the account menu
 */
export function AppShell({
  app,
  nav = [],
  actions,
  children,
}: {
  app?: string;
  nav?: NavItem[];
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="app-shell">
      <header className="plate appbar">
        <a
          href="/"
          style={{ textDecoration: "none" }}
          aria-label="CQT Ammo platform home"
        >
          <BrandLockup app={app} />
        </a>

        {nav.length > 0 ? (
          <nav className="appbar__nav" aria-label="Sections">
            {nav.map((item) => (
              <a
                key={item.href}
                className="appbar__link"
                href={item.href}
                aria-current={item.current ? "page" : undefined}
              >
                {item.label}
              </a>
            ))}
          </nav>
        ) : null}

        <div className="spacer" />
        {actions}
        {/* The switcher is also in the account menu; keeping it in the header
            makes it a one-click change, which is what people actually want. */}
        <ThemeToggle onPlate />
        <AccountMenu />
      </header>

      <main className="app-main">{children}</main>
    </div>
  );
}

/** Page heading with an optional eyebrow and right-aligned actions. */
export function PageHeader({
  eyebrow,
  title,
  actions,
}: {
  eyebrow?: string;
  title: string;
  actions?: ReactNode;
}) {
  return (
    <div className="row-between" style={{ marginBottom: 22, flexWrap: "wrap", gap: 12 }}>
      <div>
        {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
        <h1>{title}</h1>
      </div>
      {actions ? <div className="row">{actions}</div> : null}
    </div>
  );
}
